import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { StopLocation } from 'src/app/_models/state/stopState';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { RouteService } from 'src/app/services/route.service';
import { map, skipWhile, switchMap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { SubscriptionLevel } from 'src/app/auth/models/user';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AddFavoriteModalComponent } from '../../add-favorite-modal/add-favorite-modal.component';
import {
  createModalOptions,
  deleteFavoriteModalOptions,
} from 'src/app/_helpers/create-modal-options';
import { MapService } from 'src/app/services/map.service';
import TrimbleMaps, { LngLatLike } from '@trimblemaps/trimblemaps-js';
import { AddStopMarkerService } from 'src/app/services/add-stop-marker.service';
import { ModalboxComponent } from '../../modalbox/modalbox.component';
import { PlaceSetService } from 'src/app/services/place-set.service';
import { GoogleAnalytics } from 'src/app/services/google-analytics.service';

@Component({
  selector: 'app-map-addstop-marker',
  templateUrl: './map-addstop-marker.component.html',
  styles: [],
})
export class MapAddStopMarkerComponent implements OnInit, OnDestroy {
  @ViewChild('popup', { static: true }) popupElement: ElementRef;

  marker: TrimbleMaps.Marker;
  popup: TrimbleMaps.Popup;
  popupLocation$: Observable<StopLocation>;
  lngLat$: Observable<LngLatLike>;
  isLoading$ = new Observable<boolean>();
  errorMessage$ = new Observable<string>(null);
  canAddStop$: Observable<boolean>;
  isFavoritePlace$: Observable<boolean>;
  isSharedFavorite$: Observable<boolean>;
  isPremium$: Observable<boolean>; // premium user can save and remove favorite
  hasAdminRole$: Observable<boolean>; // admin user can delete shared favorite

  private sm = new SubscriptionManager();
  constructor(
    private placeSetService: PlaceSetService,
    private addStopMarkerService: AddStopMarkerService,
    private routeService: RouteService,
    private authenticationService: AuthenticationService,
    private modalService: BsModalService,
    private mapService: MapService,
    private googleAnalytics: GoogleAnalytics
  ) {
    this.popupCloseListener = this.popupCloseListener.bind(this);
  }

  ngOnInit(): void {
    this.isLoading$ = this.addStopMarkerService.isLoading$;
    this.errorMessage$ = this.addStopMarkerService.error$;
    this.sm.manage = combineLatest([
      this.mapService.map$.pipe(skipWhile((mapValue) => mapValue === null)),
      this.addStopMarkerService.lngLat$,
      this.addStopMarkerService.location$.pipe(map((value) => value.canZoom)),
    ]).subscribe(([mapValue, coords, canZoom]) => {
      if (coords) {
        this.initMarker();
        this.marker.setLngLat(coords).addTo(mapValue);
        this.popup.setLngLat(coords).addTo(mapValue);
      }
      if (canZoom) {
        mapValue.jumpTo({
          center: coords,
          zoom: 16,
        });
      }
    });

    this.popupLocation$ = this.addStopMarkerService.location$.pipe(
      map((value) => value.location)
    );
    this.canAddStop$ = combineLatest([
      this.isLoading$,
      this.routeService.selectedRoute$,
      this.errorMessage$,
    ]).pipe(
      map(
        ([isLoading, selectedRoute, errorMessage]) =>
          !isLoading && selectedRoute !== null && errorMessage === null
      )
    );
    this.isFavoritePlace$ = combineLatest([
      this.isLoading$,
      this.routeService.selectedRoute$,
      this.addStopMarkerService.location$.pipe(map((value) => value.location)),
      this.errorMessage$,
    ]).pipe(
      map(([isLoading, selectedRoute, stop, errorMessage]) => {
        return (
          !isLoading &&
          errorMessage === null &&
          stop?.isFavorite &&
          selectedRoute !== null
        );
      })
    );

    this.isSharedFavorite$ = combineLatest([
      this.isLoading$,
      this.routeService.selectedRoute$,
      this.addStopMarkerService.location$.pipe(map((value) => value.location)),
      this.errorMessage$,
    ]).pipe(
      map(([isLoading, selectedRoute, stop, errorMessage]) => {
        return (
          !isLoading &&
          errorMessage === null &&
          stop?.isSharedFavorite &&
          selectedRoute !== null
        );
      })
    );

    this.sm.manage = this.mapService.mapClick$
      .pipe(
        switchMap((evt) =>
          this.addStopMarkerService.addMarkerUsingLngLat(evt?.lngLat)
        )
      )
      .subscribe();
    this.isPremium$ = this.authenticationService.currentUser.pipe(
      map((user) => {
        return user?.subscriptionLevel === SubscriptionLevel.PREMIUM;
      })
    );
    this.hasAdminRole$ = this.authenticationService.currentUser.pipe(
      map((user) => {
        return user?.roles?.some((role) => role === 'Company Administrator');
      })
    );
  }

  initMarker() {
    this.removeMarker();
    this.marker = new TrimbleMaps.Marker();
    this.popup = new TrimbleMaps.Popup({
      closeOnClick: false,
      closeButton: true,
      offset: {
        top: [0, 0],
        'top-left': [0, 0],
        'top-right': [0, 0],
        bottom: [0, -30],
        'bottom-left': [10, -30],
        'bottom-right': [-10, -30],
        left: [10, -20],
        right: [-10, -20],
      },
    });
    this.popup?.on('close', this.popupCloseListener);
    this.popup.setDOMContent(this.popupElement.nativeElement);
  }

  popupCloseListener() {
    this.removeMarker();
  }

  removeMarker() {
    this.popup?.off('close', this.popupCloseListener);
    this.marker?.remove();
    this.popup?.remove();
    this.marker = null;
    this.popup = null;
  }

  addStop(location: StopLocation) {
    const stop: StopLocation = location;
    this.routeService.addStopToSelectedRoute(stop);
    this.removeMarker();
    this.googleAnalytics.addGA(
      'Add_Stop_To_Route',
      'Add_Stop_PopUp_Click',
      'Map_Popup',
      'Add stop from map popup'
    );
  }

  addSave(location: StopLocation) {
    this.modalService.show(
      AddFavoriteModalComponent,
      createModalOptions({ initialState: location })
    );
    this.googleAnalytics.addGA(
      'Add_Favorite',
      'Add_Favorite_Click',
      'Map_Popup',
      'Add favorite from map popup'
    );
  }

  removeFavorite(location: StopLocation) {
    const options = deleteFavoriteModalOptions({
      id: location.favoriteId,
      name: location.name,
    });
    const dialogRef = this.modalService.show<ModalboxComponent>(
      ModalboxComponent,
      options
    );
    dialogRef.content.event.subscribe((id: number) => {
      this.sm.manage = this.placeSetService.deletePlace(id).subscribe();
    });
    this.googleAnalytics.addGA(
      'Remove_Favorite',
      'Remove_Favorite_Click',
      'Map_Popup',
      'remove favorite from map popup'
    );
  }

  ngOnDestroy(): void {
    this.removeMarker();
    this.sm.unsubscribe();
  }
}
